import { useCallback, useEffect, useState } from 'react'
import { get } from './lib/api'
import { me, signOut, type User } from './lib/auth'
import { t } from './lib/i18n'
import SignIn from './components/SignIn'
import Account from './components/Account'
import Admin, { type AdminTab } from './components/Admin'
import Garage from './components/Garage'
import RecordMatch from './components/RecordMatch'
import Brackets from './components/Brackets'
import Tickets from './components/Tickets'
import Migrations from './components/Migrations'
import BugReport from './components/BugReport'
import EnvBanner from './components/EnvBanner'
import UpdateBar from './components/UpdateBar'
import InstallPrompt from './components/InstallPrompt'
import DebugPanel from './components/DebugPanel'
import ErrorBoundary from './components/ErrorBoundary'

/**
 * The tabs this application adds to the console, filtered by capability here
 * rather than inside it -- `migrations.read` is ours, defined in
 * services/api/src/appRoles.ts, and Admin.tsx has no business knowing it exists.
 *
 * ⚠️ TICKETS was written by the factory, shipped in every app, and mounted in
 * NONE of them. `Tickets.tsx` has been calling GET /api/tickets since the first
 * commit and nothing rendered it, so a bug report filed from the app went into a
 * queue with no screen -- present in the codebase and absent from the product,
 * which is the same failure the API side of it had.
 */
function adminTabs(user: User): AdminTab[] {
  const tabs: AdminTab[] = []
  if (user.capabilities.includes('operations.write')) {
    tabs.push({ id: 'tickets', label: t('admin.tickets'), render: () => <Tickets /> })
  }
  if (user.capabilities.includes('migrations.read')) {
    tabs.push({ id: 'migrations', label: t('admin.migrations'), render: () => <Migrations /> })
  }
  return tabs
}

export interface Health {
  app: string
  environment: 'local' | 'beta' | 'production'
  version: string
  commit: string
}

type Tab = 'garage' | 'race' | 'brackets' | 'account' | 'admin'

export default function App() {
  const [user, setUser] = useState<User | null>(null)
  const [health, setHealth] = useState<Health | null>(null)
  const [ready, setReady] = useState(false)
  const [tab, setTab] = useState<Tab>('garage')

  const refresh = useCallback(async () => {
    setUser(await me())
  }, [])

  useEffect(() => {
    void (async () => {
      // Both in parallel: neither depends on the other, and the boot is the one
      // moment the user is definitely waiting.
      const [u, h] = await Promise.all([
        me(),
        get<Health>('/api/health').catch(() => null),
      ])
      setUser(u)
      setHealth(h)
      setReady(true)
    })()
  }, [])

  if (!ready) return <div className="boot">{t('app.loading')}</div>

  return (
    <ErrorBoundary>
      {/* Above everything: an app running old code is the cause of bug reports
          nobody can reproduce. */}
      <UpdateBar />
      <EnvBanner health={health} />

      <header className="bar">
        <strong>{health?.app ?? 'Smash Down'}</strong>
        {user && (
          <nav>
            <button onClick={() => setTab('garage')} aria-current={tab === 'garage'}>{t('garage.title')}</button>
            <button onClick={() => setTab('race')} aria-current={tab === 'race'}>{t('race.title')}</button>
            <button onClick={() => setTab('brackets')} aria-current={tab === 'brackets'}>{t('bracket.title')}</button>
            <button onClick={() => setTab('account')} aria-current={tab === 'account'}>Account</button>
            {/* Capability-driven, so the tab is absent rather than present-and-denied. */}
            {user.capabilities.includes('admin.access') && (
              <button onClick={() => setTab('admin')} aria-current={tab === 'admin'}>{t('admin.title')}</button>
            )}
            <button onClick={async () => { await signOut(); await refresh() }}>{t('app.signOut')}</button>
          </nav>
        )}
      </header>

      <main>
        {!user && <SignIn onSignedIn={refresh} />}
        {user && tab === 'garage' && <Garage user={user} />}
        {user && tab === 'race' && <RecordMatch user={user} />}
        {user && tab === 'brackets' && <Brackets user={user} />}
        {user && tab === 'account' && <Account user={user} />}
        {/*
          * Smash Down's own admin tab, filtered HERE rather than inside the
          * console: `migrations.read` is this application's capability, defined
          * in services/api/src/appRoles.ts, and admin.tsx has no business
          * knowing it exists. See the note on `extra` there.
          */}
        {user && tab === 'admin' && <Admin extra={adminTabs(user)} />}
      </main>

      {/* Capability, not environment: the phone that needs diagnosing is always
          a real one in production. */}
      {user?.capabilities.includes('errors.read') && <DebugPanel environment={health?.environment ?? '—'} />}

      {/* Anyone signed in, on every screen. A bug door that has to be found is a
          bug door nobody uses, and the children on the tablet are the testers. */}
      {user && <BugReport page={tab} />}

      {/* Below the fold, not in the way. For an app that is not on a store yet. */}
      <InstallPrompt />

      <footer className="muted small">
        {health ? `${health.version} · ${health.commit} · ${health.environment}` : ''}
      </footer>
    </ErrorBoundary>
  )
}
